'use client';

import { 
    PaintBrushIcon, 
    UserGroupIcon, 
    BriefcaseIcon, 
    GlobeAsiaAustraliaIcon, 
    FingerPrintIcon, 
    HeartIcon, 
    HomeModernIcon,
    ChartBarSquareIcon, 
    BuildingLibraryIcon, 
    AcademicCapIcon,
    CurrencyDollarIcon, 
    CogIcon, 
    UserIcon, 
    PresentationChartLineIcon,
    PuzzlePieceIcon
} from '@heroicons/react/24/solid';
import { 
    LockClosedIcon, 
    LockOpenIcon, 
    CheckIcon, 
    XMarkIcon
} from '@heroicons/react/24/outline';
import { 
    Home01, 
    Building02, 
    Building03, 
    LayersThree01, 
    CheckDone01, 
    Data, 
    LineChartUp01, 
    Settings01, 
    BookOpen01,
    Asterisk01,
    Calculator,
    Signal03 
} from '@untitledui/icons';
import {
    Home01 as Home01Solid,
    Building02 as Building02Solid, 
    Building03 as Building03Solid, 
    LayersThree01 as LayersThree01Solid, 
    CheckDone01 as CheckDone01Solid, 
    Data as DataSolid, 
    LineChartUp01 as LineChartUp01Solid, 
    Settings01 as Settings01Solid, 
    BookOpen01 as BookOpen01Solid,
    Asterisk01 as Asterisk01Solid,
    Calculator as CalculatorSolid,
    Signal03 as Signal03Solid
} from '@untitledui-pro/icons/solid';
import sanitizeHtml from 'sanitize-html';
import PasswordValidator from 'password-validator';
import zxcvbn from 'zxcvbn';
import { lookupIcon as heroLookupIcon } from 'heroicons-lookup';

import { EMPTY_OPTION, TOTAL_OPTIONS_YEARS } from '@constants/ui';
import { Domain, ALL_DOMAINS } from '@constants/indicator';
import { ResourceType } from '@constants/resource';
import { PasswordFailChecks, PasswordIconType, PasswordStrengthUI } from '@constants/general';

let UiUtils = {};

UiUtils.isMobile = (width) => {
    return width < 1024;
};

UiUtils.isXLargeDesktop = (width) => {
    return width > 1400;
};

UiUtils.isLargeDesktop = (width) => {
    return width > 1200;
};

UiUtils.getYearOptions = () => {
    const year = new Date().getFullYear();
    let options = [EMPTY_OPTION];
    for (let y = year; y >= year - TOTAL_OPTIONS_YEARS; y-- ) {
        options.push({ value: y, label: y });
    }
    return options;
};

UiUtils.capitalise = (text, allWords) => {
    if (text.indexOf("/") > -1) {
        const parts = text.split("/");
        return parts.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join("/");
    }
    return text.charAt(0).toUpperCase() + text.slice(1);
}

UiUtils.capitaliseAllWords = (text) => {
    const parts = text.split(" ");
    return parts.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(" ");
}

UiUtils.getOptionFromValue = (value, options) => {
    let option = EMPTY_OPTION;
    const index = options.map(o => o.value).indexOf(value);
    if (index > -1) {
        option = options[index];
    }
    return option;
}

UiUtils.getOptionsFromValues = (values, options) => {
    let retOpts = [];
    values.forEach(v => {
        const index = options.map(o => o.value).indexOf(v);
        if (index > -1) {
            retOpts.push(options[index]);
        }
    });
    return retOpts;
}

UiUtils.getOptionsFromFields = (data, options) => {
    let retOpts = [];
    options.forEach(o => {
        if (data.hasOwnProperty(o.value) && data[o.value] === true) {
            retOpts.push(o);
        }
    });
    return retOpts;
}

UiUtils.setFieldsFromOptions = (data, values, options) => {
    let d = JSON.parse(JSON.stringify(data));
    options.forEach(o => {
        d[o.value] = false;
    });
    values.forEach(v => {
        d[v.value] = true;
    });
    return d;
};

const lookupIcon = (name, format) => {
    switch (name) {
        case "Home01":
            if (format === "solid") {
                return Home01Solid;
            } else {
                return Home01;
            }
        case "Building02":
            if (format === "solid") {
                return Building02Solid;
            } else {
                return Building02;
            }
        case "Building03":
            if (format === "solid") {
                return Building03Solid;
            } else {
                return Building03;
            }
        case "LayersThree01":
            if (format === "solid") {
                return LayersThree01Solid;
            } else {
                return LayersThree01;
            }
        case "CheckDone01":
            if (format === "solid") {
                return CheckDone01Solid;
            } else {
                return CheckDone01;
            }
        case "Data":
            if (format === "solid") {
                return DataSolid;
            } else {
                return Data;
            }
        case "LineChartUp01":
            if (format === "solid") {
                return LineChartUp01Solid;
            } else {
                return LineChartUp01;
            }
        case "Settings01":
            if (format === "solid") {
                return Settings01Solid;
            } else {
                return Settings01;
            }
        case "BookOpen01":
            if (format === "solid") {
                return BookOpen01Solid;
            } else {
                return BookOpen01;
            }
        case "Asterisk01":
            if (format === "solid") {
                return Asterisk01Solid;
            } else {
                return Asterisk01;
            }
        case "Calculator":
            if (format === "solid") {
                return CalculatorSolid;
            } else {
                return Calculator;
            }
        case "Signal03":
            if (format === "solid") {
                return Signal03Solid;
            } else {
                return Signal03;
            }
        default:
            return heroLookupIcon(name, format);
    }
}

UiUtils.lookupIcon = lookupIcon;

UiUtils.getDomainIcon = (domain) => {
    let icon = null;
    switch (domain) {
        case Domain.ARTS:
            icon = PaintBrushIcon;
            break;
        case Domain.COMMUNITY:
            icon = UserGroupIcon;
            break;
        case Domain.COLLABORATION:
            icon = PuzzlePieceIcon;
            break;
        case Domain.WORK:
            icon = BriefcaseIcon;
            break;
        case Domain.ENVIRONMENT:
            icon = GlobeAsiaAustraliaIcon;
            break;
        case Domain.FIRST_NATIONS:
            icon = FingerPrintIcon;
            break;
        case Domain.HEALTH:
            icon = HeartIcon;
            break;
        case Domain.HOUSING:
            icon = HomeModernIcon;
            break;
        case Domain.IMPROVING_SYSTEMS:
        case Domain.IMPROVING_SYSTEMS_ALT:
            icon = ChartBarSquareIcon;
            break;
        case Domain.EQUITY:
            icon = BuildingLibraryIcon;
            break;
        case Domain.EDUCATION:
            icon = AcademicCapIcon;
            break;
        case Domain.FINANCIAL_MANAGEMENT:
            icon = CurrencyDollarIcon;
            break;
        case Domain.PEOPLE_MANAGEMENT:
            icon = UserIcon;
            break;
        case Domain.GOVERNANCE:
            icon = CogIcon;
            break;
        case Domain.SALES_MARKETING:
        case Domain.SALES_MARKETING_ALT:
            icon = PresentationChartLineIcon;
            break;
        case Domain.DIGITAL_INCLUSION:
            icon = Signal03;
            break;
        default:
            icon = Asterisk01;
            break;
    }
    return icon;
};

UiUtils.getDomainIconData = (domain, index) => {
    let title = "Generic domain icon";
    let id = "domain-icon";
    switch (domain) {
        case Domain.ARTS:
            title = "Paint brush icon";
            id = `${id}-arts`;
            break;
        case Domain.COMMUNITY:
            title = "User group icon";
            id = `${id}-community`;
            break;
        case Domain.COLLABORATION:
            title = "Puzzle piece icon";
            id = `${id}-collaboration`;
            break;
        case Domain.WORK:
            title = "Briefcase icon";
            id = `${id}-work`;
            break;
        case Domain.ENVIRONMENT:
            title = "Globe icon";
            id = `${id}-environment`;
            break;
        case Domain.FIRST_NATIONS:
            title = "Fingerprint icon";
            id = `${id}-first-nations`;
            break;
        case Domain.HEALTH:
            title = "Heart icon";
            id = `${id}-first-nations`;
            break;
        case Domain.HOUSING:
            title = "House icon";
            id = `${id}-housing`;
            break;
        case Domain.IMPROVING_SYSTEMS:
        case Domain.IMPROVING_SYSTEMS_ALT:
            title = "Bar graph icon";
            id = `${id}-improvement`;
            break;
        case Domain.EQUITY:
            title = "Library building icon";
            id = `${id}-equity`;
            break;
        case Domain.EDUCATION:
            title = "Academic mortarboard cap icon";
            id = `${id}-education`;
            break;
        case Domain.FINANCIAL_MANAGEMENT:
            title = "Dollar icon";
            id = `${id}-finanace`;
            break;
        case Domain.PEOPLE_MANAGEMENT:
            title = "User icon";
            id = `${id}-people`;
            break;
        case Domain.GOVERNANCE:
            title = "Cog icon";
            id = `${id}-governance`;
            break;
        case Domain.SALES_MARKETING:
        case Domain.SALES_MARKETING_ALT:
            title = "Line graph presentation icon";
            id = `${id}-salesmarketing`;
            break;
        case Domain.DIGITAL_INCLUSION:
            title = "Wifi signal icon";
            id = `${id}-digital-inclusion`;
        default:
            id = `${id}-${index}`;
            break;
    }
    return { title, id };
}

UiUtils.isCustomDomain = (domain) => {
    return (ALL_DOMAINS.indexOf(domain) === -1);
}

UiUtils.checkUserAccess = (roles, object, type) => {
    let hasAccess = false;
    roles.forEach(role => {
        role.permissions.forEach(perm => {
            if (perm.objectType === object) {
                if (perm.accessTypes.indexOf(type) > -1) {
                    hasAccess = true;
                }
            }
        });
    });
    return hasAccess;
}

UiUtils.getSVGIcon = (name, fill, css) => {
    let className = css ? css : "w-6 h-6 shrink-0 grow-0";
    switch (name) {
        case "bug-slash":
            return (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512" className={className} fill={fill}>
                    <path d="M38.8 5.1C28.4-3.1 13.3-1.2 5.1 9.2S-1.2 34.7 9.2 42.9l592 464c10.4 8.2 25.5 6.3 33.7-4.1s6.3-25.5-4.1-33.7L477.4 348.9c1.7-9.4 2.6-19 2.6-28.9h64c17.7 0 32-14.3 32-32s-14.3-32-32-32H479.7c-1.1-14.1-5-27.5-11.1-39.5c.7-.6 1.4-1.2 2.1-1.9l64-64c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-64 64c-.7 .7-1.3 1.4-1.9 2.1C409.2 164.1 393.1 160 376 160H264c-8.3 0-16.3 1-24 2.8L38.8 5.1zm392 430.3L336 360.7V479.2c36.6-3.6 69.7-19.6 94.8-43.8zM166.7 227.3c-3.4 9-5.6 18.7-6.4 28.7H96c-17.7 0-32 14.3-32 32s14.3 32 32 32h64c0 24.6 5.5 47.8 15.4 68.6c-2.2 1.3-4.2 2.9-6 4.8l-64 64c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0l63.1-63.1c24.5 21.8 55.8 36.2 90.3 39.6V335.5L166.7 227.3zM320 0c-53 0-96 43-96 96v3.6c0 15.7 12.7 28.4 28.4 28.4H387.6c15.7 0 28.4-12.7 28.4-28.4V96c0-53-43-96-96-96z"/>
                </svg>
            );
        case "news":
            return (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" className={className} fill={fill}>
                    <path d="M160 64c-17.7 0-32 14.3-32 32V416c0 11.7-3.1 22.6-8.6 32H432c26.5 0 48-21.5 48-48V96c0-17.7-14.3-32-32-32H160zM64 480c-35.3 0-64-28.7-64-64V160c0-35.3 28.7-64 64-64v32c-17.7 0-32 14.3-32 32V416c0 17.7 14.3 32 32 32s32-14.3 32-32V96c0-35.3 28.7-64 64-64H448c35.3 0 64 28.7 64 64V400c0 44.2-35.8 80-80 80H64zM384 112c0-8.8 7.2-16 16-16h32c8.8 0 16 7.2 16 16s-7.2 16-16 16H400c-8.8 0-16-7.2-16-16zm0 64c0-8.8 7.2-16 16-16h32c8.8 0 16 7.2 16 16s-7.2 16-16 16H400c-8.8 0-16-7.2-16-16zm0 64c0-8.8 7.2-16 16-16h32c8.8 0 16 7.2 16 16s-7.2 16-16 16H400c-8.8 0-16-7.2-16-16zM160 304c0-8.8 7.2-16 16-16H432c8.8 0 16 7.2 16 16s-7.2 16-16 16H176c-8.8 0-16-7.2-16-16zm0 64c0-8.8 7.2-16 16-16H432c8.8 0 16 7.2 16 16s-7.2 16-16 16H176c-8.8 0-16-7.2-16-16zm32-144H320V128H192v96zM160 120c0-13.3 10.7-24 24-24H328c13.3 0 24 10.7 24 24V232c0 13.3-10.7 24-24 24H184c-13.3 0-24-10.7-24-24V120z"/>
                </svg>
            );
        case "half-star":
            return (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512" className={className} fill={fill}>
                    <path d="M288 0c-12.2 .1-23.3 7-28.6 18L195 150.3 51.4 171.5c-12 1.8-22 10.2-25.7 21.7s-.7 24.2 7.9 32.7L137.8 329 113.2 474.7c-2 12 3 24.2 12.9 31.3s23 8 33.8 2.3L288 439.8V0zM429.9 512c1.1 .1 2.1 .1 3.2 0h-3.2z"/>
                </svg>
            );
        case "cresent-moon":
            return (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 91.867 91.853" className={className} fill={fill}>
                    <path d="m45.935 91.853c25.316 0 45.932-20.603 45.932-45.925 0-25.333-20.613-45.928-45.932-45.928-25.333 0-45.935 20.595-45.935 45.928 0.0041 25.319 20.603 45.925 45.935 45.925zm0-82.352c1.2871 0 2.5563 0.06339 3.8185 0.20257-12.865 6.9592-21.645 20.554-21.645 36.223 0 15.663 8.7588 29.261 21.637 36.22-1.2526 0.13092-2.5232 0.20258-3.8104 0.20258-20.092 0-36.428-16.328-36.428-36.424 0-20.095 16.337-36.428 36.428-36.428z" strokeWidth=".35278"/>
                </svg>
            );
        case "full-moon":
            return (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 91.853 91.86" className={className} fill={fill}>
                <path d="m91.853 45.928c0-25.324-20.607-45.928-45.928-45.928-25.328 0-45.925 20.599-45.925 45.928 0 25.32 20.595 45.932 45.925 45.932 25.319 0 45.928-20.607 45.928-45.932zm-45.928 36.431c-20.084 0-36.428-16.337-36.428-36.428 0-20.088 16.345-36.424 36.428-36.424 20.088 0 36.424 16.337 36.424 36.424 0.0041 20.088-16.337 36.428-36.424 36.428z" strokeWidth=".35278"/>
                </svg>
            );
        case "half-battery":
            return (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className={className} fill={fill}>
                    <path d="M4.5 9.75a.75.75 0 0 0-.75.75V15c0 .414.336.75.75.75h6.75A.75.75 0 0 0 12 15v-4.5a.75.75 0 0 0-.75-.75H4.5Z"/>
                    <path fill-rule="evenodd" d="M3.75 6.75a3 3 0 0 0-3 3v6a3 3 0 0 0 3 3h15a3 3 0 0 0 3-3v-.037c.856-.174 1.5-.93 1.5-1.838v-2.25c0-.907-.644-1.664-1.5-1.837V9.75a3 3 0 0 0-3-3h-15Zm15 1.5a1.5 1.5 0 0 1 1.5 1.5v6a1.5 1.5 0 0 1-1.5 1.5h-15a1.5 1.5 0 0 1-1.5-1.5v-6a1.5 1.5 0 0 1 1.5-1.5h15Z" clip-rule="evenodd"/>
                </svg>
            );
        case "full-battery":
            return (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className={className} fill={fill}>
                    <path fill-rule="evenodd" d="M3.75 6.75a3 3 0 0 0-3 3v6a3 3 0 0 0 3 3h15a3 3 0 0 0 3-3v-.037c.856-.174 1.5-.93 1.5-1.838v-2.25c0-.907-.644-1.664-1.5-1.837V9.75a3 3 0 0 0-3-3h-15Zm15 1.5a1.5 1.5 0 0 1 1.5 1.5v6a1.5 1.5 0 0 1-1.5 1.5h-15a1.5 1.5 0 0 1-1.5-1.5v-6a1.5 1.5 0 0 1 1.5-1.5h15ZM4.5 9.75a.75.75 0 0 0-.75.75V15c0 .414.336.75.75.75H18a.75.75 0 0 0 .75-.75v-4.5a.75.75 0 0 0-.75-.75H4.5Z" clip-rule="evenodd"/>
                </svg>
            );
        default:
            return (<></>);
    }
}

UiUtils.sanitiseHtml = (html) => {
    return sanitizeHtml(html, {
        allowedTags: sanitizeHtml.defaults.allowedTags.concat([ 'img', 'iframe' ]),
        allowedAttributes: {
            '*': [ '*' ]
        },
        allowedIframeHostnames: ['www.youtube.com', 'www.youtube-nocookie.com'],
        allowedSchemes: sanitizeHtml.defaults.allowedSchemes.concat(['data']),
        parseStyleAttributes: false
    });
}

UiUtils.scrollToSection = (id) => {
    const element = typeof window !== "undefined" ? document.getElementById(id) : null;
    if (window && element) {
        element.scrollIntoView({
            block: "start",
            behavior: "instant",
        });
        window.location.hash = `#${id}`;
    }
}

UiUtils.adjustThumbnail = (thumbnail) => {
    let adjThumb = {
        alt: thumbnail.alt ? thumbnail.alt : "",
        images: {}
    }
    thumbnail.images.forEach(image => {
        adjThumb.images[image.imageType] = {
            url: image.url,
            s3Key: image.s3Key,
            width: image.width,
            height: image.height
        }
    })
    return adjThumb
}

UiUtils.getResourceAriaLabel = (type, title) => {
    switch (type) {
        case ResourceType.VIDEO:
            return `Watch the video for '${title}'`;
        case ResourceType.POWERPOINT:
            return `Browse the Powerpoint presentation for '${title}'`;
        default:
            return `Read the article for '${title}'`;
    }
}

const runPasswordFailCheck = (checks, fails) => {
    let found = 0;
    fails.forEach(f => {
        if (checks.indexOf(f) > -1) {
            found++;
        }
    });
    return (found !== checks.length);
}

UiUtils.runPasswordFailCheck = runPasswordFailCheck;

UiUtils.getPasswordDisplay = (password, strength, fails) => {
    const si = strength > 0 && strength < PasswordStrengthUI.length ? strength : 0;
    return (
        <div className="font-vg-book text-xs2 flex flex-col items-stretch gap-2 mb-1.5">
            <div className="flex items-center gap-2">
                {password ? (
                    <>
                        {PasswordStrengthUI[si].ICON === PasswordIconType.OPEN ? (
                            <LockOpenIcon className={`size-4 mb-1 ${PasswordStrengthUI[si].COLOR}`}/>
                        ) : (
                            <LockClosedIcon className={`size-4 mb-1 ${PasswordStrengthUI[si].COLOR}`}/>
                        )}
                        <p className={PasswordStrengthUI[si].COLOR}>
                            Password strength: {PasswordStrengthUI[si].TEXT}
                        </p>
                    </>
                ) : (
                    <>
                        <XMarkIcon className="size-4 mb-1 text-red"/>
                        <p className="text-red">Enter a password</p>
                    </>
                )}
            </div>
            {PasswordFailChecks.map((pf, index) => {
                    const ok = runPasswordFailCheck(pf.CHECKS, fails);
                    return (
                        <div key={`password-check-${index}`} className="flex items-center gap-2">
                            {ok ? (
                                <CheckIcon className="size-4 mb-1 text-green04"/>
                            ) : (
                                <XMarkIcon className="size-4 mb-1 text-red"/>
                            )}
                            <p className={ok ? "text-green04" : "text-red"}>{pf.TEXT}</p>
                        </div>
                    );
                }
            )}
        </div>
    );
}

UiUtils.getPasswordSchema = () => {
    const schema = new PasswordValidator();
    schema
    .is().min(8)
    .has().digits()
    .has().symbols()
    .has().not().spaces();
    return schema;
}

UiUtils.validatePassword = (schema, text) => {
    const v1 = schema.validate(text, { list: true });
    const v2 = zxcvbn(text);
    return {
        fails: v1,
        strength: v2.score ? v2.score : 0
    };
};

UiUtils.runPasswordFailChecks = (fails) => {
    let result = true
    PasswordFailChecks.forEach(pf => {
        const ok = runPasswordFailCheck(pf.CHECKS, fails);
        result = result && ok;
    });
    return result;
};

UiUtils.linkify = (text) => {
    let urlRegex =/(\b(https?|ftp|file):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/ig;
    return text.replace(urlRegex, function(url) {
        return '<a href="' + url + '" target="_blank" rel="noopener noreferrer" style="text-decoration:underline">' + url + '</a>';
    });
}

UiUtils.containsAnchor = (text) => {
    return /\<a.*\>.*\<\/a\>/.test(text);
}

export default UiUtils;